import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormsModule,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
} from '@angular/forms';
import { TreeNode } from 'primeng/api';
import { TreeSelect, TreeSelectModule } from 'primeng/treeselect';

import { buildSourceTree, joinSourcePath, lastTierOf, splitSourcePath } from '../source-tiers';

/** Tier under which free-text values typed into the filter are stored. */
const CUSTOM_VALUE_TIER = 'Other';

/**
 * Single-select tree picker for tiered job sources ("How did they hear about
 * us?"). Wraps PrimeNG's `p-treeSelect`. The selected value is the **full path
 * string** (e.g. `'Realtor > Remax'`), emitted via `valueChange` and exposed as
 * a `ControlValueAccessor` so it works with `[ngModel]`, `appControlMeta`, and
 * `app-validation-messages` the way the dropdowns it replaces did.
 *
 * See `docs/tiered-job-sources.md` and `../source-tiers.ts`.
 */
@Component({
  selector: 'app-source-tree-select',
  standalone: true,
  imports: [CommonModule, FormsModule, TreeSelectModule],
  templateUrl: './source-tree-select.component.html',
  styleUrls: ['./source-tree-select.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: SourceTreeSelectComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: SourceTreeSelectComponent,
    },
  ],
})
export class SourceTreeSelectComponent implements ControlValueAccessor, Validator, OnChanges, OnDestroy {

  @ViewChild(TreeSelect) private treeSelect?: TreeSelect;

  /** Flat config paths, e.g. ['Realtor', 'Realtor > Remax', 'Radio']. */
  @Input() options: string[] = [];

  /** Full path string (for non-form usage; `[ngModel]` goes through writeValue). */
  @Input() value: string | null = null;

  @Input() required = false;
  @Input() disabled = false;
  @Input() tabindex: number | undefined;
  @Input() placeholder = 'Select a source';

  /** Emits the full path string (or null on clear). */
  @Output() valueChange = new EventEmitter<string | null>();

  tree: TreeNode[] = [];
  selectedNode: TreeNode | null = null;

  /** Current filter text (trimmed). Drives the "no results" custom-value link. */
  searchTerm = '';

  /** Whether the current filter matched any node. */
  private filterHasResults = true;

  /** Teardown for the panel's Enter-key listener (only attached while open). */
  private filterKeydownUnlisten?: () => void;

  constructor(private readonly renderer: Renderer2) {}

  /**
   * Label shown for the current selection: the most specific tier only (e.g.
   * `'Betty White'` for `'Realtor > Remax > Betty White'`), not the full path.
   */
  get displayLabel(): string {
    return this.selectedNode ? lastTierOf(this.selectedNode.key as string) : '';
  }

  /** The current full-path value, normalized. Source of truth for the display. */
  private currentValue: string | null = null;

  // CONTROL VALUE ACCESSOR
  private onChange: (value: string | null) => void = () => {};
  onTouched: () => void = () => {};

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.options) {
      this.tree = buildSourceTree(this.options || []);
    }

    if (changes.value) {
      // `value` Input changed (non-ngModel usage) — keep in sync.
      this.setValue(this.value ?? null, false);
    } else if (changes.options) {
      // Re-resolve the selected node against the (possibly new) tree.
      this.syncSelectedNode();
    }
  }

  /** Selecting a node (leaf or non-leaf) selects its full path. */
  onNodeSelect(event: { node: TreeNode }): void {
    const path = (event.node?.key as string) ?? null;
    this.setValue(path, true);
  }

  onClear(): void {
    this.setValue(null, true);
  }

  /** Track the filter text and whether it matched anything (see template). */
  onFilter(event: { filter: string; filteredValue: TreeNode[] | null }): void {
    this.searchTerm = (event.filter ?? '').trim();
    this.filterHasResults = !!event.filteredValue?.length;
  }

  /** Full path a custom (typed) value resolves to, e.g. `'Other > Facebook'`. */
  get customValuePath(): string {
    return joinSourcePath([CUSTOM_VALUE_TIER, this.searchTerm]);
  }

  /**
   * Adopt the typed filter text as a custom value under `Other`. Invoked from
   * the "no results" link and by pressing Enter while it is showing.
   */
  enterCustomValue(): void {
    if (!this.searchTerm) { return; }
    this.setValue(this.customValuePath, true);
    this.treeSelect?.hide();
  }

  /** Panel opened — listen for Enter on the filter input (it lives in the overlay). */
  onPanelShow(): void {
    const input = this.treeSelect?.filterViewChild?.nativeElement;
    if (!input) { return; }
    this.filterKeydownUnlisten = this.renderer.listen(input, 'keydown', (event: KeyboardEvent) => {
      // Mirror the "no results" link: only when there's a term and nothing matched.
      if (event.key === 'Enter' && this.searchTerm && !this.filterHasResults) {
        event.preventDefault();
        this.enterCustomValue();
      }
    });
  }

  /** Panel closed — mark as touched and reset filter state (filter clears on hide). */
  onPanelHide(): void {
    this.onTouched();
    this.teardownFilterKeydown();
    this.searchTerm = '';
    this.filterHasResults = true;
  }

  ngOnDestroy(): void {
    this.teardownFilterKeydown();
  }

  private teardownFilterKeydown(): void {
    this.filterKeydownUnlisten?.();
    this.filterKeydownUnlisten = undefined;
  }

  // CONTROL VALUE ACCESSOR IMPLEMENTATION
  writeValue(value: string | null): void {
    this.setValue(value ?? null, false);
  }

  registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.required && !this.currentValue) {
      return { required: true };
    }
    return null;
  }

  /**
   * Update the current value, refresh the displayed node, and (when this came
   * from a user interaction) notify the form / parent.
   */
  private setValue(rawValue: string | null, emit: boolean): void {
    const normalized = rawValue ? joinSourcePath(splitSourcePath(rawValue)) : null;
    this.currentValue = normalized || null;
    this.syncSelectedNode();

    if (emit) {
      this.onChange(this.currentValue);
      this.onTouched();
      this.valueChange.emit(this.currentValue);
    }
  }

  /**
   * Point `selectedNode` at the tree node matching the current value. If the
   * value is not in the config (historical/intake-written value), append a
   * synthetic node so TreeSelect can still display the selection.
   */
  private syncSelectedNode(): void {
    if (!this.currentValue) {
      this.selectedNode = null;
      return;
    }

    const match = this.findNode(this.tree, this.currentValue);
    if (match) {
      this.selectedNode = match;
      return;
    }

    // Value not in config — render it anyway via a synthetic node.
    this.selectedNode = {
      key: this.currentValue,
      label: this.currentValue,
      data: this.currentValue,
    };
  }

  private findNode(nodes: TreeNode[], key: string): TreeNode | null {
    for (const node of nodes) {
      if (node.key === key) { return node; }
      if (node.children?.length) {
        const found = this.findNode(node.children, key);
        if (found) { return found; }
      }
    }
    return null;
  }
}
